import { Injectable, Optional } from '@angular/core';

import { ActivatedRoute, ActivatedRouteSnapshot, Router } from '@angular/router';
import { SerializedRouterStateSnapshot } from '@ngrx/router-store';
import { Store } from '@ngrx/store';
import { AuthenticationFacade } from '@planone/bau365/data-access-authentication';
import * as CookieConsentActions from '@planone/shared/feature-cookie-consent';
import { DocumentRef, WindowRef } from '@planone/shared/util-browser-api';
import { CookiehandlingService } from '@planone/shared/util-cookie-handling';

import * as GoogleAnalyticsActions from '../state/google-analytics.actions';
import { GoogleAnalyticsConfiguration, GoogleAnalyticsEvent } from '../state/google-analytics.models';

@Injectable()
export class GoogleAnalyticsService {
  private trackedElements: HTMLElement[] = [];
  private excludeLinkUrls: string[] = [];
  private eventQueue: GoogleAnalyticsEvent[] = [];
  private anonymousEvents: string[] = ['pageview_consent'];

  constructor(
    private windowRef: WindowRef,
    private documentRef: DocumentRef,
    private store: Store,
    private router: Router,
    private route: ActivatedRoute,
    @Optional() private authenticationFacade: AuthenticationFacade,
    private cookieHandlingService: CookiehandlingService
  ) {}

  processQueue(): void {
    if (this.getCookieConsentStatus() === true) {
      this.eventQueue.forEach((event: GoogleAnalyticsEvent) => this.push(event));

      this.eventQueue = [];
    }
  }

  initialize(configuration: GoogleAnalyticsConfiguration): void {
    this.windowRef.nativeWindow.dataLayer = this.windowRef.nativeWindow.dataLayer || [];

    (function (w: Window & any, d: Document, s: any, l: any, i: any) {
      w[l] = w[l] || [];
      w[l].push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
      const f = d.getElementsByTagName(s)[0],
        j = d.createElement(s),
        dl = l != 'dataLayer' ? '&l=' + l : '';
      j.async = true;
      j.src = 'https://www.googletagmanager.com/gtm.js?id=' + i + dl;
      f.parentNode.insertBefore(j, f);
    })(this.windowRef.nativeWindow, this.documentRef.nativeDocument, 'script', 'dataLayer', configuration.propertyId);

    const targetNode = this.documentRef.nativeDocument.querySelector('#body');

    if (targetNode) {
      new MutationObserver(() => this.linkClickObserverCallback()).observe(targetNode, {
        childList: true,
        attributes: true,
        subtree: true,
      });
    }

    this.excludeLinkUrls = configuration.excludeLinkUrls;
  }

  push(event: GoogleAnalyticsEvent): void {
    if (this.getCookieConsentStatus() === undefined && (!event.event || !this.anonymousEvents.includes(event.event))) {
      this.eventQueue.push(event);
    } else {
      this.windowRef.nativeWindow.dataLayer?.push(event);
    }
  }

  extractData(route: SerializedRouterStateSnapshot | ActivatedRouteSnapshot | undefined | null): any {
    let child = route?.root.firstChild;
    let data = {};

    if (child) {
      while (child?.firstChild) {
        child = child.firstChild;
        data = { ...data, ...child.data };
      }
    }

    return data;
  }

  getPageType(url: string): string {
    const urlSegments = new URL('http://test.local/' + url).pathname.split('/');
    const pageType = urlSegments[urlSegments.length - 1] ?? '';

    if (pageType.length === 0) {
      return 'home';
    }

    return 'unknown';
  }

  isExcludedFromClickTracking(url?: string): boolean {
    return this.excludeLinkUrls.find((excludeURL) => url?.startsWith(excludeURL)) ? true : false;
  }

  private linkClickObserverCallback() {
    Array.from(this.documentRef?.nativeDocument?.querySelectorAll('a') ?? [])
      .filter((element: any) => {
        const url = element.getAttribute('href');
        const filter = /^(tel:|mailto:|http(s)?:\/\/)(.+)$/g;
        const data = this.extractData(this.route.snapshot);

        /**
         * Filter trackable URLs by the following conditions:
         * - Only allow anchor tag clicks which do not have customTracking=true set on the DOM element
         * - Only allow tracking if current route is not custom tracked
         * - Only allow external hyperlink-references starting with "tel:", "mailto:" or "http(s)://"
         */
        return (
          !(data?.googleAnalytics?.customTracking && !data?.googleAnalytics?.customPageviewOnly) &&
          ((url && url.match(filter)) ||
            element.classList.contains('cc-allow') ||
            element.classList.contains('cc-deny')) &&
          element.getAttribute('customTracking') !== 'true'
        );
      })
      .forEach((element: any) => {
        if (!this.trackedElements.includes(element)) {
          if (element.classList.contains('cc-allow')) {
            element.addEventListener('click', (_: any) =>
              this.store.dispatch(CookieConsentActions.changed({ status: true }))
            );
          } else if (element.classList.contains('cc-deny')) {
            element.addEventListener('click', (_: any) =>
              this.store.dispatch(CookieConsentActions.changed({ status: false }))
            );
          } else {
            element.addEventListener('click', (event: any) => this.linkClickListener(event));
            element.addEventListener('mouseup', (event: any) => {
              if (event.which === 2) {
                this.linkClickListener(event);
              }
            });
          }

          this.trackedElements.push(element);
        }
      });
  }

  private linkClickListener(event: any) {
    const url = event.currentTarget.getAttribute('href');
    const content =
      event.currentTarget.getAttribute('analyticsLinkText') &&
      event.currentTarget.getAttribute('analyticsLinkText').length > 0
        ? event.currentTarget.getAttribute('analyticsLinkText')
        : event.currentTarget.innerHTML ?? '';
    const classes = <string[]>Array.from(event.currentTarget.classList);
    const id = event.currentTarget.getAttribute('id');
    // const target = event.currentTarget.getAttribute('target');
    const linkType = event.currentTarget.getAttribute('analyticsLinkType') ?? 'EXTERNAL_LINK';

    if (url && !this.isExcludedFromClickTracking(url)) {
      const data = this.extractData(this.route.snapshot);
      const pageType = data?.analyticsPageType ?? this.getPageType(this.router.url);

      let exclude_analytics = true;

      this.authenticationFacade.excludeAnalytics$.subscribe((status) => (exclude_analytics = status)).unsubscribe();

      this.store.dispatch(
        GoogleAnalyticsActions.push({
          event: 'click',
          context: this.route.snapshot.params['fair_id'],
          link_type: linkType,
          page_type: pageType,
          link_classes: classes,
          link_id: id,
          link_url: url,
          link_text: content,
          exclude_analytics: exclude_analytics,
          outbound: true,
        })
      );
    }
  }

  private getCookieConsentStatus() {
    const cookie = this.cookieHandlingService.read('CookieConsent');

    return cookie !== '' ? cookie === 'allow' : undefined;
  }
}
